import { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  DocumentCheckIcon,
  CheckCircleIcon,
  GlobeAltIcon,
  ScaleIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  UserGroupIcon,
  ClipboardDocumentCheckIcon,
  ArrowsPointingInIcon,
  EnvelopeIcon,
  MagnifyingGlassIcon,
  AcademicCapIcon,
  Squares2X2Icon,
  BoltIcon,
  ArrowsRightLeftIcon,
  KeyIcon,
  PuzzlePieceIcon,
  LockClosedIcon,
  AdjustmentsHorizontalIcon,
  RocketLaunchIcon,
  ChatBubbleBottomCenterTextIcon,
} from '@heroicons/react/24/outline';

/* ─── Animation Helpers ─── */
const ease = [0.44, 0, 0.56, 1] as const;

function FadeUp({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay, ease }} className={className}>
      {children}
    </motion.div>
  );
}

function FadeIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}} transition={{ duration: 0.7, delay, ease }} className={className}>
      {children}
    </motion.div>
  );
}

function ScaleIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, scale: 0.95 }} animate={isInView ? { opacity: 1, scale: 1 } : {}} transition={{ duration: 0.6, delay, ease }} className={className}>
      {children}
    </motion.div>
  );
}

/* ─── Data ─── */

const sinceApril22 = [
  {
    title: 'VBAA Peer Comparison Engine for R1 Redlining',
    description:
      'Per-AA, per-year, per-MMCT-band redlining screen with chi-squared significance testing and Volume-Based Adjusted Aggregate peer comparison. Built to the methodology spec we received from a Federal Reserve fair-lending SME on April 15.',
  },
  {
    title: 'Regulator-Aware MMCT Classification',
    description:
      'Fair lending engine now applies the correct majority-minority census tract formula per regulator: FRB uses (Black + Hispanic) / Total ≥ 50.01%, FDIC/OCC/NCUA use the all-minorities ≥ 50% formula. Branch distribution analysis (R12) honors the team\'s primary regulator end-to-end.',
  },
  {
    title: 'CRA Lending Analysis Hardening',
    description:
      'Required Assessment Area selection on every analysis run, surfaced unclassified-tract loan counts, removed silent fallback to current calendar year, and added analysis-level deletion so consultants can clean up stale work product before exam season.',
  },
  {
    title: 'Multi-Bank Operator Console',
    description:
      'A new top-level dashboard view (/organization) for advisory firms managing portfolios: aggregate health scores, CRA ratings, overdue tasks, and exam countdowns across all client institutions, with click-through into any bank\'s full analytical depth.',
  },
  {
    title: 'Branches as a First-Class Entity',
    description:
      'Branch and LPO data drives both R12 redlining and CRA assessment-area validation. New top-level navigation, FDIC BankFind integration with Census geocoding enrichment, Wallace-spec import template, and edit/delete capabilities for ongoing maintenance.',
  },
];

// Reordered to lead with the three topics for tomorrow's conversation:
// HMDA, Fair Lending, CRA — then the supporting capabilities.
const analyticalCapabilities = [
  {
    icon: DocumentCheckIcon,
    title: 'HMDA LAR Validation & Data Integrity',
    description:
      'Upload, validate against 170+ CFPB edit checks (S/V/Q/M series), and identify data integrity issues automatically. AI-powered plain-language explanations for every finding. Field mapping for LOS imports, geocoding validation, and submission-ready exports. Turns a multi-day manual HMDA review into a same-day deliverable.',
  },
  {
    icon: ScaleIcon,
    title: 'Fair Lending Statistical Analysis',
    description:
      'Full IFLEP-aligned Fair Lending Dashboard with 21 risk factors across underwriting, pricing, steering, redlining, and marketing. VBAA peer comparison for redlining analysis, chi-squared/Fisher\'s exact/Welch\'s t-test significance testing, 9 prohibited basis groups, 5 loan purpose segments. The same analytical framework examiners use — automated.',
  },
  {
    icon: ClipboardDocumentCheckIcon,
    title: 'CRA Program Evaluation',
    description:
      'Assessment area delineation with census tract demographics. Lending Test analysis (geographic + borrower income distribution). Community development activity tracking. AI-powered gap analysis with cross-module correlations. Size-adaptive framework (Small/Intermediate/Large). Exam readiness scoring that updates as data flows in.',
  },
  {
    icon: ChartBarIcon,
    title: 'Regression Analysis Engine',
    description:
      'Logistic regression for denial and steering models. OLS regression for pricing disparity analysis. Matched-pair analysis for examiner file review. Regulator-aware output formatted for FDIC, OCC, Fed, and NCUA standards. Full methodology documentation for examiner defensibility — produced automatically, not manually assembled.',
  },
  {
    icon: MagnifyingGlassIcon,
    title: 'Marketing Compliance & UDAAP',
    description:
      'AI-powered content scanning with deterministic rules engine verification. UDAAP 3-dimensional risk scoring (Unfair/Deceptive/Abusive 0-100). Approval workflows with human-in-the-loop review. Immutable audit trail with SHA-256 integrity hashing. Website crawling, social media checking, and enforcement action cross-reference.',
  },
  {
    icon: GlobeAltIcon,
    title: 'Regulatory Intelligence & Exam Packaging',
    description:
      'Automated monitoring of enforcement actions from CFPB, OCC, FDIC. One-click examiner-ready ZIP exports and board reports that pull from every module — fair lending findings, HMDA data quality, marketing compliance stats, CRA performance, regression results — all aggregated automatically with methodology documentation.',
  },
];

const directOperatorCapabilities = [
  {
    icon: Squares2X2Icon,
    title: 'Multi-Bank Operator Console',
    description:
      'A single dashboard across every client institution: aggregate health scores, exam countdowns, overdue tasks, and risk signals at a glance. Click into any bank for the full analytical depth. Built specifically for advisory firms managing portfolios — not retrofitted from a single-bank product.',
  },
  {
    icon: AdjustmentsHorizontalIcon,
    title: 'Targeted Reviews, Not Comprehensive Sweeps',
    description:
      'Clarid pre-identifies the focal points worth examining — protected-class disparities passing significance thresholds, peer-relative redlining outliers, CRA lending test gaps, geographic concentration anomalies. Arcsalus consultants don\'t triage every client portfolio quarterly; they review the focal points Clarid surfaces and apply judgment where it matters.',
  },
  {
    icon: BoltIcon,
    title: 'Capacity Multiplier, Not Replacement',
    description:
      'The platform is designed to be operated at depth by analysts who don\'t necessarily have 15-year FL/CRA backgrounds. Senior practitioners review Clarid\'s findings and apply expert interpretation; the rest of the team operates the platform under their guidance. Capacity bottleneck becomes capacity expansion.',
  },
  {
    icon: AcademicCapIcon,
    title: 'Clarid Academy — Structured Training Program',
    description:
      'A Arcsalus-specific training and certification program: platform operation, examiner methodology, finding interpretation, and exam-readiness packaging. Builds bench depth from Arcsalus\'s existing team, creates a recurring revenue stream tied to Arcsalus\'s headcount, and standardizes how the platform is operated across the firm.',
  },
];

const consultantBenefits = [
  {
    icon: ShieldCheckIcon,
    title: 'Deeper Engagements, Faster Delivery',
    description:
      'When Arcsalus conducts HMDA data integrity reviews, fair lending assessments, or CRA evaluations, Clarid provides the analytical engine that automates the data work. Your consultants focus on interpretation, strategy, and client guidance — not data wrangling. A fair lending review that takes 2 weeks manually becomes a same-week deliverable.',
  },
  {
    icon: UserGroupIcon,
    title: 'Continuous Value Between Engagements',
    description:
      'Consulting engagements end. The compliance program keeps running. Clarid stays with the client after the engagement, providing continuous monitoring and automated testing that maintains the standards Arcsalus helped establish. Your clients see Arcsalus as the firm that set them up for ongoing success.',
  },
  {
    icon: AcademicCapIcon,
    title: 'Technology That Speaks Examiner',
    description:
      'Every output Clarid produces is designed for examiner consumption — because it was built by people who sat on both sides of the examination table. The methodology documentation, statistical notation, risk classification, and report formatting match what regulators expect. When your clients present Clarid outputs during exams, examiners recognize the framework.',
  },
  {
    icon: ChartBarIcon,
    title: 'Scale the Practice Without Scaling Headcount',
    description:
      'Clarid\'s automated analytics let your team serve more clients at greater depth without proportional headcount growth. The platform handles the computational work — your people handle the judgment calls that make Arcsalus\'s advisory indispensable. The economics of the consulting practice change in your favor.',
  },
];

const interconnectivityFlows = [
  {
    title: 'HMDA Data Integrity Feeds Fair Lending',
    description: 'LAR entries validated through HMDA checks are enriched with 50+ derived columns and fed directly into denial rate, pricing, and geographic disparity analyses. One data upload, multiple analytical outputs.',
  },
  {
    title: 'Fair Lending Findings Power Regression',
    description: 'Statistical disparities identified in the Fair Lending Dashboard are validated through logistic and OLS regression analysis, adding multivariate controls that strengthen examiner defensibility.',
  },
  {
    title: 'CRA Lending Analysis Uses HMDA Originations',
    description: 'CRA lending tests pull origination volumes and geographic distribution from the same LAR data, with assessment area mapping and income-level classification — no separate data preparation.',
  },
  {
    title: 'Branch Data Drives Redlining and AA Validation',
    description: 'Branch and LPO locations enrich R12 branch distribution analysis (with regulator-correct MMCT classification) and validate CRA assessment area coverage. One branch import, two analytical outputs.',
  },
  {
    title: 'Everything Rolls Into Exam Packages',
    description: 'Fair lending findings, HMDA data quality scores, CRA performance metrics, regression results, and marketing compliance audit trails — all aggregated into a single examiner-ready export. One click.',
  },
  {
    title: 'Board Reports Aggregate Every Module',
    description: 'Quarterly board reports pull compliance health scores, fair lending risk levels, CRA ratings, regression findings, and regulatory alerts into an executive summary — ready for the board package your clients need.',
  },
];

const partnershipModels = [
  {
    num: '01',
    title: 'Direct Operator Across Arcsalus\'s Portfolio',
    description:
      'Arcsalus licenses Clarid as the analytical platform their team operates across the entire client portfolio. The Multi-Bank Operator Console gives Arcsalus one view across every institution; targeted-review focal points flag the work that needs senior judgment; the rest of the team executes under expert oversight. This is the model with the largest economic and operational leverage for Arcsalus.',
  },
  {
    num: '02',
    title: 'Analytical Engine for Consulting Engagements',
    description:
      'Arcsalus consultants use Clarid as the analytical platform during compliance assessments, fair lending reviews, HMDA data integrity testing, and CRA evaluations. The technology handles data processing and statistical analysis — your team handles interpretation, client communication, and strategic guidance. Faster delivery, deeper analysis, same trusted advisory relationship.',
  },
  {
    num: '03',
    title: 'Client Retention Through Continuous Monitoring',
    description:
      'After a Arcsalus engagement, the client deploys Clarid for ongoing compliance monitoring. The platform maintains the standards and practices Arcsalus established — automated testing, continuous risk scoring, examiner-ready documentation produced as a byproduct of normal operations. Arcsalus stays connected as the advisory partner; Clarid keeps the program running between engagements.',
  },
  {
    num: '04',
    title: 'Co-Branded Engagement Bundle',
    description:
      'Arcsalus packages Clarid into specific consulting deliverables — for example, "Arcsalus HMDA & Fair Lending Review, Powered by Clarid Analytics." Joint go-to-market with co-branding rights. Clear identity for Arcsalus, technology depth from Clarid, and a productized offering that\'s easier for client banks to buy and easier for Arcsalus to scale.',
  },
];

const commercialModels = [
  {
    icon: ArrowsRightLeftIcon,
    title: 'Channel Referral with Revenue Share',
    description:
      'Arcsalus refers Clarid to client banks during or after engagements. Revenue share on subscription term. Standard SaaS partner channel structure, examiner-friendly because Arcsalus stays in the advisory role and Clarid stays in the technology role.',
  },
  {
    icon: KeyIcon,
    title: 'Direct Firm License',
    description:
      'Arcsalus licenses Clarid as the analytical platform the firm operates across the client portfolio. Pricing scales with portfolio depth and analytical surface, not seat count. This is the model best aligned with the Direct Operator partnership vision and the model where Arcsalus captures the most operating leverage.',
  },
  {
    icon: PuzzlePieceIcon,
    title: 'Co-Branded Productized Engagements',
    description:
      'Joint go-to-market for specific deliverables — Fair Lending Reviews, HMDA Integrity Audits, CRA Performance Evaluations — with co-branded reporting and shared sales motion. Productizing a deliverable typically expands the addressable market because banks can buy a defined scope rather than commission a custom engagement.',
  },
  {
    icon: LockClosedIcon,
    title: 'Targeted or Tiered Exclusivity',
    description:
      'We are open to discussing exclusivity in defined dimensions — geography, client-size band (e.g., community banks under a specified asset threshold), or sector specialization (e.g., certain regulator combinations). Targeted exclusivity is something we can move on quickly. Total exclusivity is a longer conversation tied to growth commitments and our broader pipeline.',
  },
  {
    icon: AcademicCapIcon,
    title: 'Training and Certification Revenue',
    description:
      'Clarid Academy with structured curriculum for Arcsalus staff. Revenue split on training program. Increases bench capacity for Arcsalus, deepens product fluency, creates a recurring revenue stream tied to Arcsalus headcount, and standardizes how the platform is operated across the firm.',
  },
];

const dueDiligenceDocs = [
  'AI Governance Policy',
  'FS AI RMF Gap Analysis (230 control objectives, 100% coverage)',
  'Model Inventory with validation framework',
  'Bias Testing Methodology & Results',
  'Data Flow Architecture Documentation',
  'Information Security Program (WISP)',
  'Incident Response Plan',
  'Business Continuity Plan',
  'Vendor Inventory (third-party dependencies)',
];

const nextSteps = [
  { num: 1, title: 'April 22 — Initial Conversation', description: 'Established alignment on advisory + analytical fit. Surfaced the bench-capacity reality and the direct-operator opportunity. Identified HMDA, Fair Lending, and CRA as the core demonstration topics.', complete: true },
  { num: 2, title: 'April 28 — Today', description: 'Live walkthrough across HMDA validation, fair lending statistical analysis, CRA evaluation, redlining (R1 + R12), and the Multi-Bank Operator Console. Discuss commercial model preferences and exclusivity scope.' },
  { num: 3, title: 'Joint Pilot — 2 to 3 Arcsalus Client Banks', description: 'Structured pilot with selected Arcsalus clients. Arcsalus provides advisory expertise and the client relationship; Clarid provides the analytical engine and onboarding support. Defined success metrics. 60- to 90-day cycle.' },
  { num: 4, title: 'Commercial Framework', description: 'Lock in the model — channel referral, direct firm license, co-branded productized engagements, exclusivity tier, training program — or some combination. Definitive partnership agreement and commercial terms.' },
  { num: 5, title: 'Operating Cadence', description: 'Quarterly partnership review, joint client roadmap, training program rollout, shared product feedback loop. Long-term we want Arcsalus as a strategic advisor on Clarid\'s product direction in the FL/CRA/HMDA segments.' },
];

/* ─── Component ─── */
export default function ArcsalusPartnerPage() {
  useEffect(() => {
    document.title = 'Arcsalus Advisors + Clarid — Compliance Analytics for Advisory Excellence';

    const metaTags: Record<string, string> = {
      'og:title': 'Arcsalus Advisors + Clarid — Compliance Analytics for Advisory Excellence',
      'og:description': 'AI-native compliance analytics platform powering deeper, faster consulting engagements for financial institutions.',
      'og:type': 'website',
      'og:image': '/clarid-logo.png',
    };

    const existingTags: HTMLMetaElement[] = [];
    for (const [property, content] of Object.entries(metaTags)) {
      const meta = document.createElement('meta');
      meta.setAttribute('property', property);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
      existingTags.push(meta);
    }

    return () => {
      existingTags.forEach((tag) => document.head.removeChild(tag));
      document.title = 'Clarid — Clarity in every communication';
    };
  }, []);

  return (
    <div className="min-h-screen bg-white font-body text-slate-600">
      {/* ─── Minimal Header ─── */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 py-5 flex items-center">
          <div className="flex items-center gap-5">
            <img src="/arcsalus-logo.png" alt="Arcsalus Advisors" className="h-10" />
            <span className="text-slate-300 text-xl font-light">|</span>
            <img src="/clarid-logo.png" alt="Clarid" className="h-10" />
          </div>
        </div>
      </header>

      {/* ─── Section 1: Hero ─── */}
      <section className="pt-36 pb-20 md:pt-48 md:pb-28">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 text-center">
          <FadeUp>
            <p className="text-base font-semibold tracking-[0.2em] uppercase text-emerald-600 mb-8">
              Strategic Partnership · Continuing Our April 22 Conversation
            </p>
          </FadeUp>

          <FadeUp delay={0.1}>
            <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-[5.5rem] font-display font-bold text-navy-900 leading-[1.1] mb-10 max-w-5xl mx-auto">
              Built for the Partnership You're Looking For
            </h1>
          </FadeUp>

          <FadeUp delay={0.2}>
            <p className="text-xl md:text-2xl lg:text-[1.65rem] text-slate-500 max-w-4xl mx-auto leading-relaxed">
              Arcsalus Advisors brings deep regulatory expertise and trusted relationships across a substantial
              community-bank client base. Clarid brings the AI-native analytical engine — HMDA validation,
              fair lending statistical analysis, CRA evaluation, regression modeling, and a Multi-Bank Operator
              Console designed specifically for advisory firms managing portfolios. Together, advisory depth
              with analytical horsepower at examiner standard.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─── Section 2: Following Our April 22 Conversation ─── */}
      <section className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <div className="flex items-center justify-center gap-3 mb-8">
                <ChatBubbleBottomCenterTextIcon className="h-8 w-8 text-emerald-600" />
                <p className="text-base font-semibold tracking-[0.2em] uppercase text-emerald-600">
                  What We Heard on April 22
                </p>
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-14">
                The Bench-Capacity Reality
              </h2>
            </FadeUp>

            <FadeUp delay={0.1}>
              <div className="space-y-7">
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  Two themes came through in our first conversation. First, Arcsalus's bench — broadened
                  by the Velligan-Blaxall merger to span regulatory, risk, and balance-sheet advisory —
                  is exactly the kind of expertise community banks rely on: examination experience, decades
                  of community-bank advisory practice, and real depth across HMDA, fair lending, CRA, and
                  BSA/AML. Second, the work Arcsalus does on behalf of those banks is bottlenecked by capacity:
                  with a substantial portfolio of client institutions and only a small subset of staff
                  currently set up to run fair-lending or CRA reviews end-to-end, the question isn't
                  whether Arcsalus has the expertise — it's how to give that expertise more reach.
                </p>
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  That capacity pattern isn't unique to Arcsalus. It's a structural characteristic of the
                  advisory market: few firms have enough fair-lending or CRA practitioners to give every
                  client the depth a focal-point review demands. And it's exactly the gap Clarid is built
                  to close.
                </p>
                <p className="text-xl md:text-[1.35rem] font-semibold text-navy-900 mt-10">
                  This page reflects what we've thought through since April 22, and how we've sharpened
                  the platform in response.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── Section 3: Since April 22 — Velocity Proof ─── */}
      <section className="py-24 md:py-32">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <div className="text-center mb-14">
              <div className="flex items-center justify-center gap-3 mb-5">
                <RocketLaunchIcon className="h-8 w-8 text-emerald-600" />
                <p className="text-base font-semibold tracking-[0.2em] uppercase text-emerald-600">
                  Six Days, Real Cadence
                </p>
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 mb-8">
                What We've Shipped Since April 22
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-500 max-w-5xl mx-auto leading-relaxed">
                We are a small, fast company. The advantage of that for Arcsalus is velocity: when something
                we discuss matters, it ships. The advantage for end-customer banks is that the platform
                gets sharper every week. Below — verifiable in our git history — is what advanced between
                our first conversation and today.
              </p>
            </div>
          </FadeUp>

          <div className="max-w-5xl mx-auto space-y-5 lg:space-y-6">
            {sinceApril22.map((item, i) => (
              <ScaleIn key={item.title} delay={i * 0.08}>
                <div className="p-8 lg:p-10 rounded-2xl bg-emerald-50/40 border border-emerald-100 hover:bg-emerald-50/70 hover:border-emerald-200 transition-all duration-500">
                  <div className="flex items-start gap-5">
                    <CheckCircleIcon className="h-7 w-7 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-xl md:text-2xl font-display font-bold text-navy-900 mb-2">{item.title}</h3>
                      <p className="text-lg text-slate-600 leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                </div>
              </ScaleIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Section 4: The Opportunity ─── */}
      <section className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-14">
                The Gap Between Advisory Insight and Analytical Scale
              </h2>
            </FadeUp>

            <FadeUp delay={0.1}>
              <div className="space-y-7">
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  Compliance advisory firms like Arcsalus deliver exactly what financial institutions
                  need — expert assessment, regulatory interpretation, and hands-on guidance from
                  people who understand the examination process. But the analytical work underneath
                  those assessments — HMDA data validation, fair lending statistical testing,
                  CRA lending analysis, regression modeling — is computationally intensive and
                  time-consuming when done manually.
                </p>
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  A fair lending assessment requires pulling LAR data, enriching it with demographic
                  and geographic classifications, running denial-rate and pricing comparisons across
                  9 prohibited basis groups and 5 loan purpose segments, computing statistical
                  significance, and documenting the methodology — all before the consultant even
                  begins the interpretive work that makes the engagement valuable.
                </p>
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  Clarid automates that analytical layer. Your consultants arrive at the interpretation
                  faster, go deeper into the data, and deliver findings with statistical rigor that
                  matches what examiners produce internally — because it was built by people who used
                  to produce those findings as examiners.
                </p>
                <p className="text-xl md:text-[1.35rem] font-semibold text-navy-900 mt-10">
                  Arcsalus provides the expertise. Clarid provides the analytical engine. Both Arcsalus
                  and the end-customer bank get a deeper, faster, more defensible result.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── Section 5: Analytical Capabilities ─── */}
      <section className="py-24 md:py-32">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 mb-6">
                What Clarid Brings to the Table
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-500 max-w-5xl mx-auto leading-relaxed">
                Six capability areas, organized around the work Arcsalus consultants do for client banks.
                The first three — HMDA, Fair Lending, CRA — are the ones we'll walk through together today.
              </p>
            </div>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {analyticalCapabilities.map((cap, i) => (
              <ScaleIn key={cap.title} delay={i * 0.1}>
                <div className="group p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 hover:border-emerald-200 hover:shadow-xl transition-all duration-500 h-full">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center group-hover:bg-emerald-100 group-hover:scale-110 transition-all duration-300">
                      <cap.icon className="h-6 w-6 text-emerald-600" />
                    </div>
                    <h3 className="text-2xl font-display font-bold text-navy-900">{cap.title}</h3>
                  </div>
                  <p className="text-lg text-slate-500 leading-relaxed">{cap.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Section 6: Arcsalus as a Direct Operator ─── */}
      <section className="py-24 md:py-32 bg-navy-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '48px 48px',
        }} />

        <div className="relative max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <div className="text-center mb-14">
              <p className="text-base font-semibold tracking-[0.2em] uppercase text-emerald-300 mb-5">
                The Direct-Operator Vision
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-white mb-8">
                Beyond Engagements: Arcsalus as a Direct Operator
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-400 max-w-5xl mx-auto leading-relaxed">
                Most compliance-tech vendors think of consulting partners as resellers. We don't. The most
                operationally interesting model — and the one where Arcsalus captures the most leverage — is
                Arcsalus as a direct user of Clarid, operating across the full client portfolio. The
                Multi-Bank Operator Console is built specifically for this.
              </p>
            </div>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10 mt-14">
            {directOperatorCapabilities.map((cap, i) => (
              <ScaleIn key={cap.title} delay={i * 0.08}>
                <div className="group p-10 lg:p-12 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/[0.08] hover:border-white/20 transition-all duration-500 h-full">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-emerald-500/15 flex items-center justify-center group-hover:bg-emerald-500/25 group-hover:scale-110 transition-all duration-300">
                      <cap.icon className="h-6 w-6 text-emerald-400" />
                    </div>
                    <h3 className="text-2xl font-display font-bold text-white">{cap.title}</h3>
                  </div>
                  <p className="text-lg text-slate-300 leading-relaxed">{cap.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Section 7: How Arcsalus's Practice Benefits ─── */}
      <section className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <div className="text-center mb-14">
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 mb-8">
                How Arcsalus's Practice Benefits
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-500 max-w-5xl mx-auto leading-relaxed">
                Whether Arcsalus uses Clarid as a direct operator across the portfolio or as the analytical
                engine inside individual engagements, the practice-level benefits compound the same way:
                faster delivery, deeper analysis, longer client relationships.
              </p>
            </div>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {consultantBenefits.map((benefit, i) => (
              <ScaleIn key={benefit.title} delay={i * 0.08}>
                <div className="group p-10 lg:p-12 bg-white rounded-2xl border border-slate-100 hover:border-emerald-200 hover:shadow-xl transition-all duration-500 h-full">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center group-hover:bg-emerald-100 group-hover:scale-110 transition-all duration-300">
                      <benefit.icon className="h-6 w-6 text-emerald-600" />
                    </div>
                    <h3 className="text-2xl font-display font-bold text-navy-900">{benefit.title}</h3>
                  </div>
                  <p className="text-lg text-slate-500 leading-relaxed">{benefit.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Section 8: Cross-Module Interconnectivity ─── */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <div className="text-center mb-14">
              <p className="text-base font-semibold tracking-[0.2em] uppercase text-emerald-600 mb-5">
                The Differentiator
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 mb-8">
                Cross-Module Interconnectivity
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-500 max-w-5xl mx-auto leading-relaxed">
                Most compliance analytics tools are siloed — HMDA validation separate from fair lending,
                CRA separate from everything. Clarid connects every module so a single data upload triggers
                analysis across the entire compliance program. For consultants, this means one engagement
                yields findings across multiple compliance domains simultaneously.
              </p>
            </div>
          </FadeUp>

          <div className="space-y-5 lg:space-y-6 max-w-5xl mx-auto">
            {interconnectivityFlows.map((flow, i) => (
              <ScaleIn key={flow.title} delay={i * 0.06}>
                <div className="p-8 lg:p-10 rounded-2xl bg-slate-50 border border-slate-100 hover:border-emerald-200 hover:bg-white hover:shadow-md transition-all duration-500">
                  <div className="flex items-center gap-4 mb-3">
                    <ArrowsPointingInIcon className="h-6 w-6 text-emerald-600 flex-shrink-0" />
                    <h3 className="text-xl font-display font-bold text-navy-900">{flow.title}</h3>
                  </div>
                  <p className="text-lg text-slate-500 leading-relaxed pl-10">{flow.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Section 9: Examination Perspective ─── */}
      <section className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-14">
              Shared DNA — Built by Examiners, for Examiners
            </h2>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            <ScaleIn delay={0.1}>
              <div className="p-10 lg:p-12 bg-white rounded-2xl border border-slate-100 hover:shadow-xl transition-all duration-500 h-full">
                <h3 className="text-2xl font-display font-bold text-navy-900 mb-5">
                  Clarid's Examination Partners
                </h3>
                <p className="text-lg text-slate-500 leading-relaxed">
                  A former FDIC Supervisory Examiner and a former Federal Reserve Supervisory
                  Examiner and fair lending subject matter expert are partners in Clarid.
                  Every rule engine, every statistical test, every output format is validated
                  against what examiners actually evaluate. The outputs speak their language
                  because the people who built them used to write the examination reports.
                </p>
              </div>
            </ScaleIn>

            <ScaleIn delay={0.2}>
              <div className="p-10 lg:p-12 bg-white rounded-2xl border border-slate-100 hover:shadow-xl transition-all duration-500 h-full">
                <h3 className="text-2xl font-display font-bold text-navy-900 mb-5">
                  Arcsalus's Regulatory Bench
                </h3>
                <p className="text-lg text-slate-500 leading-relaxed">
                  Arcsalus's leadership combines FDIC examination experience, Big 4 advisory backgrounds,
                  and decades of community-bank compliance practice across lending, deposits, fair
                  lending, HMDA, CRA, and BSA/AML. The same examination perspective that shapes Arcsalus's
                  advisory practice shaped Clarid's analytical engine. The alignment isn't coincidental —
                  it's why the outputs work together out of the box.
                </p>
              </div>
            </ScaleIn>
          </div>
        </div>
      </section>

      {/* ─── Section 10: Partnership Models ─── */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-6">
                Four Models for How This Works Together
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-500 text-center max-w-4xl mx-auto mb-16 leading-relaxed">
                Operationally, there's no single right way for Arcsalus and Clarid to combine. The four
                models below are designed to be combined, sequenced, or chosen based on what fits the
                practice today and where Arcsalus wants to be in 18 months.
              </p>
            </FadeUp>

            <div className="space-y-6 lg:space-y-8">
              {partnershipModels.map((model, i) => (
                <ScaleIn key={model.num} delay={i * 0.1}>
                  <div className="p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 hover:border-emerald-200 hover:bg-white hover:shadow-xl transition-all duration-500">
                    <div className="flex items-start gap-6">
                      <span className="text-3xl font-display font-bold text-emerald-600/60 flex-shrink-0 mt-0.5">
                        {model.num}
                      </span>
                      <div>
                        <h3 className="text-2xl font-display font-bold text-navy-900 mb-3">{model.title}</h3>
                        <p className="text-lg text-slate-500 leading-relaxed">{model.description}</p>
                      </div>
                    </div>
                  </div>
                </ScaleIn>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Section 11: Commercial Models ─── */}
      <section className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <div className="text-center mb-14">
              <p className="text-base font-semibold tracking-[0.2em] uppercase text-emerald-600 mb-5">
                Open for Discussion
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 mb-8">
                Commercial Models We've Thought Through
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-500 max-w-5xl mx-auto leading-relaxed">
                We are a small, fast company in active commercialization. We can adapt the commercial
                structure quickly to whatever fits Arcsalus's strategic positioning. The five models
                below are not mutually exclusive — most strong partnerships combine two or three.
              </p>
            </div>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10 max-w-7xl mx-auto">
            {commercialModels.map((model, i) => (
              <ScaleIn key={model.title} delay={i * 0.08}>
                <div className="group p-10 lg:p-12 bg-white rounded-2xl border border-slate-100 hover:border-emerald-200 hover:shadow-xl transition-all duration-500 h-full">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center group-hover:bg-emerald-100 group-hover:scale-110 transition-all duration-300">
                      <model.icon className="h-6 w-6 text-emerald-600" />
                    </div>
                    <h3 className="text-2xl font-display font-bold text-navy-900">{model.title}</h3>
                  </div>
                  <p className="text-lg text-slate-500 leading-relaxed">{model.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>

          <FadeUp delay={0.4}>
            <p className="mt-14 text-center text-lg md:text-xl text-slate-500 italic max-w-4xl mx-auto leading-relaxed">
              We'd welcome a conversation about which combination — and which timeline — fits Arcsalus's
              strategic positioning. Exclusivity in defined dimensions is something we can move on quickly.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─── Section 12: Due Diligence ─── */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-6">
                We've Done the Homework
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-500 text-center max-w-4xl mx-auto mb-14 leading-relaxed">
                Clarid maintains a comprehensive vendor due diligence package designed to meet
                interagency guidance on third-party relationships. Available on request:
              </p>
            </FadeUp>

            <FadeUp delay={0.1}>
              <div className="max-w-3xl mx-auto">
                <ul className="space-y-5">
                  {dueDiligenceDocs.map((doc, i) => (
                    <FadeIn key={doc} delay={0.15 + i * 0.05}>
                      <li className="flex items-start gap-4">
                        <CheckCircleIcon className="h-6 w-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span className="text-lg text-slate-600">{doc}</span>
                      </li>
                    </FadeIn>
                  ))}
                </ul>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── Section 13: Where We Go From Here ─── */}
      <section className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-16">
                Where We Go From Here
              </h2>
            </FadeUp>

            <div className="space-y-10 lg:space-y-12">
              {nextSteps.map((step, i) => (
                <FadeUp key={step.num} delay={i * 0.12}>
                  <div className="flex items-start gap-7">
                    <div className={`flex-shrink-0 w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold ${
                      step.complete
                        ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-200'
                        : 'bg-navy-900 text-white'
                    }`}>
                      {step.complete ? <CheckCircleIcon className="h-7 w-7" /> : step.num}
                    </div>
                    <div className="pt-2">
                      <h3 className={`text-xl md:text-2xl font-display font-bold mb-2 ${step.complete ? 'text-emerald-700' : 'text-navy-900'}`}>
                        {step.title}
                      </h3>
                      <p className="text-lg text-slate-500 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Section 14: Contact ─── */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 text-center">
          <FadeIn>
            <div className="space-y-3">
              <p className="text-3xl font-display font-bold text-navy-900">Bobby Sloan</p>
              <p className="text-lg text-slate-500">Founder & CEO, Clarid</p>
              <div className="flex items-center justify-center gap-3 mt-4 text-lg text-emerald-600">
                <EnvelopeIcon className="h-5 w-5" />
                <a href="mailto:bobby@clarid.ai" className="hover:text-emerald-700 transition-colors">
                  bobby@clarid.ai
                </a>
              </div>
              <p className="text-base text-slate-400 mt-1">clarid.ai</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ─── Minimal Footer ─── */}
      <footer className="py-8 border-t border-slate-100">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src="/arcsalus-logo.png" alt="Arcsalus Advisors" className="h-7 opacity-40" />
            <span className="text-slate-200">|</span>
            <img src="/clarid-logo.png" alt="Clarid" className="h-7 opacity-40" />
          </div>
          <p className="text-slate-400 text-sm">
            &copy; {new Date().getFullYear()} Clarid. Confidential.
          </p>
        </div>
      </footer>
    </div>
  );
}
